import React from 'react';
import { Grid, Button, TextField } from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import AppForm from 'components/Shared/AppForm';
import BaseAutocomplete from 'components/Shared/Base/BaseAutocomplete';
import AppSwitch from 'components/Shared/AppSwitch';

import { useAuth } from 'hooks';
import { useGetDDLAccountListQuery } from 'store/services/accountApi';
import { useLazyGetDDLEntityListQuery } from 'store/services/entityApi';
import { useLazyGetDDLBranchListQuery } from 'store/services/branchApi';

const INITIAL_STATE = {
  account: '',
  entity: '',
  routeName: '',
  description: '',
  origin: '',
  destination: '',
  isActive: true,
};

const RouteShipmentForm = ({
  isBusy = false,
  routeShipmentDetail = null,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
    },
  });

  const _watchEntity = useWatch({ control, name: 'entity' });
  const _watchAccount = useWatch({ control, name: 'account' });

  // Account & Entity
  const { data: accounts = [], isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();

  const [fetchEntities, { data: entities = [], isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();

  // Branches
  const [fetchBranches, { data: branches = [], isLoading: loadingBranches }] =
    useLazyGetDDLBranchListQuery();

  // Effects
  React.useEffect(() => {
    if (!routeShipmentDetail) return;
    reset({
      account: routeShipmentDetail.account,
      entity: routeShipmentDetail.entity,
      routeName: routeShipmentDetail.routeName,
      description: routeShipmentDetail.description,
      origin: routeShipmentDetail.origin,
      destination: routeShipmentDetail.destination,
      isActive: routeShipmentDetail.isActive,
    });
  }, [routeShipmentDetail, reset]);

  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({ account: _watchAccount?._id ?? viewEntity?.entity?.account?._id });
    }
  }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin, viewEntity?.entity?.account?._id]);

  React.useEffect(() => {
    if (_watchEntity?._id) {
      fetchBranches({ account: _watchAccount?._id, entity: _watchEntity?._id });
    }
  }, [_watchAccount?._id, _watchEntity?._id, fetchBranches]);

  const handleOnSubmit = (data) => {
    const payload = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      origin: data?.origin?._id,
      destination: data?.destination?._id,
    };
    onSubmit(payload);
  };

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: 'Please select an account.' }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ''}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue('entity', '');
                      setValue('origin', '');
                      setValue('destination', '');
                    }}
                    getOptionLabel={(option) => option?.name ?? ''}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={accounts}
                    disabled={isAccountAdmin}
                    loading={loadingAccounts}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: 'Please select an entity.' }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Entity"
                    required
                    value={field.value ?? ''}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue('origin', '');
                      setValue('destination', '');
                    }}
                    getOptionLabel={(option) => option?.name ?? ''}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={entities}
                    loading={loadingEntities}
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item md={6}>
            <Controller
              name="origin"
              control={control}
              rules={{ required: 'Please select an origin branch.' }}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  label="Origin Branch"
                  required
                  value={field.value ?? ''}
                  onChange={(_, data) => field.onChange(data)}
                  getOptionLabel={(option) => `${option?.name ?? ''} - ${option?.code ?? ''}`}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={branches}
                  loading={loadingBranches}
                  helperText={errors?.origin?.message}
                  error={!!errors?.origin}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="destination"
              control={control}
              rules={{ required: 'Please select a destination branch.' }}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  label="Destination Branch"
                  required
                  value={field.value ?? ''}
                  onChange={(_, data) => field.onChange(data)}
                  getOptionLabel={(option) => `${option?.name ?? ''} - ${option?.code ?? ''}`}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={branches}
                  loading={loadingBranches}
                  helperText={errors?.destination?.message}
                  error={!!errors?.destination}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="routeName"
              control={control}
              rules={{ required: 'Route name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  size="small"
                  label="Route Name"
                  error={!!errors?.routeName}
                  helperText={errors?.routeName?.message}
                />
              )}
            />
          </Grid>
          <Grid item md={12}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  size="small"
                  label="Description"
                  rows={3}
                />
              )}
            />
          </Grid>
          {routeShipmentDetail && (
            <Grid item md={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit(handleOnSubmit)}>
          {routeShipmentDetail ? 'Update' : 'Add'}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default RouteShipmentForm;